// src/applicants/ApplicantModal.js
import { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Alert,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Autocomplete,
  Chip,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Badge as BadgeIcon,
  Wc as GenderIcon,
  Public as CountryIcon,
  Work as ProfessionIcon,
  CloudUpload as CloudUploadIcon,
  Visibility as VisibilityIcon,
  Delete as DeleteIcon,
  AttachFile as AttachFileIcon,
} from "@mui/icons-material";
import api from '../config/api';
import useStore from "../config/store";
import { useTranslation } from "react-i18next";
import { COUNTRIES_BY_LANGUAGE } from "../data/countries";

// Listas de opciones por idioma
const OPTIONS_BY_LANGUAGE = {
  es: {
    countries: COUNTRIES_BY_LANGUAGE.es,
    professions: [
      "Contador", "Administrador", "Antropólogo", "Arquitecto",
      "Artista", "Astrónomo", "Astrofísico", "Auditor",
      "Biólogo", "Bioquímico", "Administrador de Empresas", "Chef",
      "Químico", "Ingeniero Civil", "Especialista en Comunicación", "Consultor",
      "Analista de Datos", "Científico de Datos", "Dentista", "Diseñador",
      "Desarrollador", "Economista", "Electricista", "Ingeniero",
      "Ingeniero Ambiental", "Diseñador Gráfico", "Historiador", "Diseñador Industrial",
      "Ingeniero Industrial", "Periodista", "Abogado", "Especialista en Marketing",
      "Matemático", "Ingeniero Mecánico", "Músico", "Enfermero",
      "Nutricionista", "Terapeuta Ocupacional", "Farmacéutico", "Filósofo",
      "Fotógrafo", "Fisioterapeuta", "Médico", "Físico",
      "Politólogo", "Psicólogo", "Relaciones Públicas", "Trabajador Social",
      "Sociólogo", "Desarrollador de Software", "Ingeniero de Software", "Estadístico",
      "Cirujano", "Analista de Sistemas", "Profesor", "Técnico",
      "Traductor", "Urbanista", "Veterinario", "Otro",
    ],
  },
  en: {
    countries: COUNTRIES_BY_LANGUAGE.en,
    professions: [
      "Accountant", "Administrator", "Anthropologist", "Architect",
      "Artist", "Astronomer", "Astrophysicist", "Auditor",
      "Biologist", "Biochemist", "Business Administrator", "Chef",
      "Chemist", "Civil Engineer", "Communication Specialist", "Consultant",
      "Data Analyst", "Data Scientist", "Dentist", "Designer",
      "Developer", "Economist", "Electrician", "Engineer", "Environmental Engineer", "Graphic Designer", "Historian", "Industrial Designer",
      "Industrial Engineer", "Journalist",
      "Lawyer", "Marketing Specialist", "Mathematician", "Mechanical Engineer", "Musician", "Nurse",
      "Nutritionist", "Occupational Therapist", "Pharmacist", "Philosopher", "Photographer", "Physical Therapist",
      "Physician", "Physicist", "Political Scientist", "Psychologist", "Public Relations", "Social Worker",
      "Sociologist", "Software Developer", "Software Engineer", "Statistician",
      "Surgeon", "Systems Analyst", "Teacher", "Technician", "Translator", "Urban Planner",
      "Veterinarian", "Other",
    ],
  },
};

const ApplicantModal = ({
  open,
  onClose,
  mode,
  selectedApplicant,
  onSave,
  user,
  authToken,
  showAlert,
  positions,
  loadingPositions,
}) => {
  const [formData, setFormData] = useState({
    name: "",
    names: "",
    firstLastName: "",
    secondLastName: "",
    email: "",
    phone: "",
    rut: "",
    gender: "",
    country: "",
    profession: "",
    currentPosition: "",
    cvUrl: null,
    positions: [],
  });

  // Ficha sin correo (PLAN_IMPORTACION_COMPUTRABAJO, fase 0): el correo que
  // dio la persona quedó pendiente porque es de otra ficha. Solo en estas
  // fichas se puede escribir el correo, y el resto se guarda aunque falte
  const withoutEmail = mode !== "create" && Boolean(selectedApplicant) && !selectedApplicant.email;

  const [validationErrors, setValidationErrors] = useState([]);
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [selectedCvFile, setSelectedCvFile] = useState(null);
  // Al reemplazar el CV, el resumen IA se elimina por defecto; el usuario puede mantenerlo
  const [keepCvSummary, setKeepCvSummary] = useState(false);
  const [pendingCvDeletion, setPendingCvDeletion] = useState(false);
  const fileInputRef = useRef(null);

  const { project } = useStore.getState();
  const { t, i18n } = useTranslation();

  // Profesiones ordenadas alfabéticamente según el idioma, con "Otro"/"Other"
  // siempre al final. El campo además es freeSolo: permite escribir una
  // carrera que no esté en la lista.
  const OTHER_PROFESSION = i18n.language === "en" ? "Other" : "Otro";
  const professionOptions = [
    ...(OPTIONS_BY_LANGUAGE[i18n.language]?.professions || OPTIONS_BY_LANGUAGE.es.professions)
      .filter((p) => p !== OTHER_PROFESSION)
      .sort((a, b) => a.localeCompare(b, i18n.language)),
    OTHER_PROFESSION,
  ];

  const createApplicant = async (applicantData) => {
    const response = await api.post(
      "/applicants",
      applicantData,
      { headers: { Authorization: `Bearer ${authToken}` } }
    );
    return response.data;
  };

  const updateApplicant = async (id, applicantData) => {
    const response = await api.put(
      `/applicants/${id}`,
      applicantData,
      { headers: { Authorization: `Bearer ${authToken}` } }
    );
    return response.data;
  };

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateForm = () => {
    const errors = [];

    if (!formData.name || formData.name.trim() === "") {
      errors.push(t("applicants.validateNameExist"));
    } else if (formData.name.trim().length < 2) {
      errors.push(t("applicants.validateNameLength"));
    }

    if (!formData.email || formData.email.trim() === "") {
      // Ficha sin correo: puede seguir sin correo (queda pendiente)
      if (!withoutEmail) errors.push(t("applicants.validateEmailExist"));
    } else if (!isValidEmail(formData.email.trim())) {
      errors.push(t("applicants.validateEmailFormat"));
    }

    setValidationErrors(errors);
    return errors.length === 0;
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files[0]) {
      selectCvFile(files[0]);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      selectCvFile(file);
    }
  };

  const selectCvFile = (file) => {
    const allowedExtensions = ["pdf", "doc", "docx"];
    const fileExtension = file.name.split(".").pop()?.toLowerCase();

    if (!fileExtension || !allowedExtensions.includes(fileExtension)) {
      showAlert(t("applicants.cvInvalidFormat"), "error");
      return;
    }

    setSelectedCvFile(file);
    setPendingCvDeletion(false);
    showAlert(t("applicants.cvSelectedWillUploadOnSave"), "info");
  };

  const uploadCvToApplicant = async (applicantId, file) => {
    try {
      const formDataToSend = new FormData();
      formDataToSend.append("cv", file);
      // El backend elimina el resumen IA al reemplazar el CV salvo que se pida mantenerlo
      if (keepCvSummary) {
        formDataToSend.append("keepCvSummary", "true");
      }

      const response = await api.post(
        `/applicants/${applicantId}/cv`,
        formDataToSend,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.data?.response?.status_response === "OK") {
        const cvUrl = response.data.response.data?.cvUrl || response.data.response.data?.cvURL;
        return { success: true, cvUrl };
      } else {
        return { success: false };
      }
    } catch (err) {
      console.error("Error uploading CV:", err);
      return { success: false };
    }
  };

  const handleDeleteCv = () => {
    setPendingCvDeletion(true);
    setFormData((prev) => ({ ...prev, cvUrl: null }));
    showAlert(t("applicants.cvWillDeleteOnSave"), "info");
  };

  const handleRemoveSelectedFile = () => {
    setSelectedCvFile(null);
    setKeepCvSummary(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleCancelDeletion = () => {
    setPendingCvDeletion(false);
    setFormData((prev) => ({
      ...prev,
      cvUrl: selectedApplicant?.cvUrl || selectedApplicant?.cvURL || selectedApplicant?.cv_url || null
    }));
  };

  useEffect(() => {
    if (!open) return;

    setValidationErrors([]);
    setShowValidationErrors(false);
    setSelectedCvFile(null);
    setPendingCvDeletion(false);
    setKeepCvSummary(false);

    if (mode === "create") {
      setFormData({
        name: "",
        names: "",
        firstLastName: "",
        secondLastName: "",
        email: "",
        phone: "",
        rut: "",
        gender: "",
        country: "",
        profession: "",
        currentPosition: "",
        cvUrl: null,
        positions: [],
      });
      return;
    }

    if (selectedApplicant) {
      setFormData({
        name: selectedApplicant.name || "",
        names: selectedApplicant.names || "",
        firstLastName: selectedApplicant.firstLastName || "",
        secondLastName: selectedApplicant.secondLastName || "",
        // Ficha sin correo: el campo viene vacío y el correo pendiente se
        // muestra debajo. Casi siempre es de otra ficha (por eso quedó sin
        // correo): proponerlo obligaba a borrarlo para guardar (2026-10-08)
        email: selectedApplicant.email || "",
        phone: selectedApplicant.phone || "",
        rut: selectedApplicant.rut || "",
        gender: selectedApplicant.gender || "",
        country: selectedApplicant.country || "",
        profession: selectedApplicant.profession || "",
        currentPosition: selectedApplicant.currentPosition || "",
        cvUrl: selectedApplicant.cvUrl || selectedApplicant.cvURL || selectedApplicant.cv_url || null,
        positions: selectedApplicant.positions || [],
      });
    }
  }, [open, mode, selectedApplicant]);

  const handleClose = () => {
    setValidationErrors([]);
    setShowValidationErrors(false);
    setSelectedCvFile(null);
    setPendingCvDeletion(false);
    onClose();
  };

  const composeFullName = (names, firstLastName, secondLastName) => {
    const clean = (v) => String(v || "").trim().replace(/\s+/g, " ");
    return [clean(names), clean(firstLastName), clean(secondLastName)].filter(Boolean).join(" ");
  };

  const handleInputChange = (field, value) => {
    let processedValue = value;

    // Partes del nombre: recomponen name automáticamente (regla global:
    // name SIEMPRE se deriva de las partes cuando existen; si quedan vacías,
    // name conserva su valor actual — registros legados)
    if (field === "names" || field === "firstLastName" || field === "secondLastName") {
      const capitalized = value.toLowerCase().replace(/\b\w/g, (l) => l.toUpperCase());
      setFormData((prev) => {
        const next = { ...prev, [field]: capitalized };
        const composed = composeFullName(next.names, next.firstLastName, next.secondLastName);
        return composed ? { ...next, name: composed } : next;
      });
      return;
    }

    if (field === "phone") {
      processedValue = value.trim();
    } else if (field === "email") {
      processedValue = value.toLowerCase().trim();
    } else if (field === "name") {
      processedValue = value
        .toLowerCase()
        .replace(/\b\w/g, (l) => l.toUpperCase());
    } else if (field === "rut") {
      processedValue = value.trim();
    }

    setFormData((prev) => ({ ...prev, [field]: processedValue }));

    if (showValidationErrors) setShowValidationErrors(false);
  };

  const handlePositionsChange = (event, newValue) => {
    const formattedPositions = newValue.map((pos) => ({
      positionId: pos.id || pos.uid,
      positionName: pos.name,
      positionAnswers: [],
    }));

    const lastPosition = newValue.length > 0 ? newValue[newValue.length - 1].name : "";

    setFormData((prev) => ({
      ...prev,
      positions: formattedPositions,
      currentPosition: lastPosition
    }));
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      setShowValidationErrors(true);
      return;
    }

    try {
      setLoading(true);
      let response;

      const cleanData = {
        name: formData.name.trim(),
        names: formData.names?.trim() || null,
        firstLastName: formData.firstLastName?.trim() || null,
        secondLastName: formData.secondLastName?.trim() || null,
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        rut: formData.rut.trim(),
        gender: formData.gender || null,
        country: formData.country?.trim() || null,
        profession: formData.profession?.trim() || null,
        currentPosition: formData.currentPosition?.trim() || null,
        cvUrl: formData.cvUrl || null,
        positions: formData.positions,
        lastUpdatedBy: user?.email || user?.uid || "unknown",
        projectId: project,
      };
      // Ficha sin correo que se guarda sin asignarle uno: el correo no se envía
      if (withoutEmail && !cleanData.email) delete cleanData.email;

      if (mode === "create") {
        response = await createApplicant(cleanData);
      } else if (mode === "edit" && selectedApplicant) {
        response = await updateApplicant(
          selectedApplicant.id || selectedApplicant.uid,
          cleanData
        );
      }

      if (response?.response?.status_response === "OK") {
        const applicantId = response?.response?.data?.id || response?.response?.data?.uid || selectedApplicant?.id || selectedApplicant?.uid;

        if (pendingCvDeletion && !selectedCvFile && applicantId) {
          try {
            const deleteResponse = await api.delete(
              `/applicants/${applicantId}/cv`,
              { headers: { Authorization: `Bearer ${authToken}` } }
            );

            if (deleteResponse.data?.response?.status_response === "OK") {
              showAlert(t("applicants.cvDeleteSuccess"), "success");
            }
          } catch (deleteErr) {
            console.error("Error deleting CV:", deleteErr);
            showAlert(t("applicants.cvDeleteError"), "warning");
          }
        }

        if (selectedCvFile && applicantId) {
          const uploadResult = await uploadCvToApplicant(applicantId, selectedCvFile);
          if (uploadResult.success) {
            showAlert(t("applicants.cvUploadSuccess"), "success");
          } else {
            showAlert(t("applicants.applicantSavedButCvFailed"), "warning");
          }
        }

        handleClose();
        onSave?.();
        // Ficha sin correo que recibió uno: sus postulaciones "Por validar"
        // por correo pasaron a su etapa
        const validatedCount = response?.response?.validatedApplications || 0;
        showAlert(
          validatedCount > 0
            ? t("applicants.emailAssignedValidated", { count: validatedCount })
            : t(mode === "create" ? "applicants.applicantCreated" : "applicants.applicantUpdated"),
          "success"
        );
      } else if (response?.response?.status_response === "NOK") {
        const errorMessage = response?.response?.message || "";
        const emailHolder = response?.response?.code === "DUPLICATE_EMAIL" ? response?.response?.data?.existingApplicant : null;
        if (errorMessage.toLowerCase().includes("rut already exists")) {
          const existingName = response?.response?.data?.existingApplicant?.name || "";
          showAlert(t("applicants.rutAlreadyExists", { name: existingName }), "error");
        } else if (emailHolder) {
          showAlert(t("applicants.emailInOtherApplicant", { email: cleanData.email || "", name: emailHolder.name || "—" }), "error");
        } else if (response?.response?.code === "EMAIL_LOCKED") {
          // La ficha recibió correo mientras el formulario estaba abierto
          showAlert(t("applicants.emailNotEditable"), "error");
        } else if (errorMessage.toLowerCase().includes("already exists")) {
          showAlert(t("applicants.emailAlreadyExists"), "error");
        } else {
          showAlert(t("applicants.applicantSaveError"), "error");
        }
        setShowValidationErrors(false);
      } else {
        showAlert(t("applicants.applicantSaveError"), "error");
        setShowValidationErrors(false);
      }
    } catch (err) {
      const errorMessage = err?.response?.data?.response?.message || "";
      const emailHolder = err?.response?.data?.response?.code === "DUPLICATE_EMAIL" ? err?.response?.data?.response?.data?.existingApplicant : null;
      if (errorMessage.toLowerCase().includes("rut already exists")) {
        const existingName = err?.response?.data?.response?.data?.existingApplicant?.name || "";
        showAlert(t("applicants.rutAlreadyExists", { name: existingName }), "error");
      } else if (emailHolder) {
        showAlert(t("applicants.emailInOtherApplicant", { email: formData.email.trim(), name: emailHolder.name || "—" }), "error");
      } else if (errorMessage.toLowerCase().includes("already exists")) {
        showAlert(t("applicants.emailAlreadyExists"), "error");
      } else {
        showAlert(t("applicants.applicantSaveError"), "error");
      }
      console.error("Error saving applicant:", err);
    } finally {
      setLoading(false);
    }
  };

  const canSubmit = formData.name.trim() !== "" && (withoutEmail || formData.email.trim() !== "");
  const canViewCv = Boolean(formData.cvUrl) && !pendingCvDeletion;
  const canUploadCv = mode !== "view";
  const showUploadZone = !canViewCv && canUploadCv;

  const selectedPositionsObjects = positions.filter((pos) =>
    formData.positions.some((fp) => fp.positionId === (pos.id || pos.uid))
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {mode === "create" && t("applicants.newApplicant")}
        {mode === "edit" && t("applicants.editApplicant")}
        {mode === "view" && t("applicants.showApplicant")}
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
          {showValidationErrors && validationErrors.length > 0 && mode !== "view" && (
            <Alert severity="error">
              <Typography variant="subtitle2">{t("validationErrors")}:</Typography>
              <ul style={{ margin: 0, paddingLeft: "20px" }}>
                {validationErrors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </Alert>
          )}

          {/* Nombre por PARTES (regla global 2026-08-14): name se compone
              desde ellas; en registros legados sin partes, name se muestra
              intacto hasta que se llenen. */}
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <TextField
              label={`${t("applicantNames")} *`}
              value={formData.names}
              onChange={(e) => handleInputChange("names", e.target.value)}
              sx={{ flex: 1, minWidth: 180 }}
              disabled={mode === "view"}
              placeholder="Juan Carlos"
            />
            <TextField
              label={`${t("applicantFirstLastName")} *`}
              value={formData.firstLastName}
              onChange={(e) => handleInputChange("firstLastName", e.target.value)}
              sx={{ flex: 1, minWidth: 160 }}
              disabled={mode === "view"}
              placeholder="Pérez"
            />
            <TextField
              label={t("applicantSecondLastName")}
              value={formData.secondLastName}
              onChange={(e) => handleInputChange("secondLastName", e.target.value)}
              sx={{ flex: 1, minWidth: 160 }}
              disabled={mode === "view"}
              placeholder="González"
            />
          </Box>
          <TextField
            label={t("fullName")}
            value={formData.name}
            fullWidth
            disabled
            helperText={t("fullNameComposedHint")}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonIcon color="action" />
                </InputAdornment>
              ),
            }}
          />

          <TextField
            label={withoutEmail ? t("email") : `${t("email")} *`}
            type="email"
            value={formData.email}
            onChange={(e) => handleInputChange("email", e.target.value)}
            fullWidth
            disabled={mode === "view" || (mode === "edit" && !withoutEmail)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailIcon color="action" />
                </InputAdornment>
              ),
            }}
            placeholder="juan.perez@email.com"
            helperText={
              withoutEmail
                ? (mode === "edit"
                  ? (selectedApplicant?.pendingEmail
                    ? t("applicants.noEmailHelperPending", { email: selectedApplicant.pendingEmail })
                    : t("applicants.noEmailHelper"))
                  : t("applicants.noEmailView", { email: selectedApplicant?.pendingEmail || "—" }))
                : mode === "edit"
                  ? t("applicants.emailNotEditable")
                  : mode !== "view"
                    ? t("applicants.validEmail")
                    : ""
            }
            FormHelperTextProps={withoutEmail ? { sx: { color: "warning.main" } } : undefined}
          />

          <TextField
            label={`${t("phone")}`}
            value={formData.phone}
            onChange={(e) => handleInputChange("phone", e.target.value)}
            fullWidth
            disabled={mode === "view"}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PhoneIcon color="action" />
                </InputAdornment>
              ),
            }}
            placeholder="+56987654321 o 987654321"
          />

          <TextField
            label={`${t("uniqueIdentifier")}`}
            value={formData.rut}
            onChange={(e) => handleInputChange("rut", e.target.value)}
            fullWidth
            disabled={mode === "view"}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <BadgeIcon color="action" />
                </InputAdornment>
              ),
            }}
            placeholder="12345678-9, DNI123456, etc."
            helperText={mode !== "view" ? t("applicants.uniqueIdentifierHelper") : ""}
          />

          <FormControl fullWidth disabled={mode === "view"}>
            <InputLabel>{t("applicants.gender")}</InputLabel>
            <Select
              value={formData.gender}
              onChange={(e) => handleInputChange("gender", e.target.value)}
              label={t("applicants.gender")}
              startAdornment={
                <InputAdornment position="start">
                  <GenderIcon color="action" />
                </InputAdornment>
              }
            >
              <MenuItem value="">{t("select")}</MenuItem>
              <MenuItem value="male">{t("applicants.genderMale")}</MenuItem>
              <MenuItem value="female">{t("applicants.genderFemale")}</MenuItem>
              <MenuItem value="other">{t("applicants.genderOther")}</MenuItem>
              <MenuItem value="preferNotToSay">{t("applicants.genderPreferNotToSay")}</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth disabled={mode === "view"}>
            <InputLabel>{t("applicants.country")}</InputLabel>
            <Select
              value={formData.country}
              onChange={(e) => handleInputChange("country", e.target.value)}
              label={t("applicants.country")}
              startAdornment={
                <InputAdornment position="start">
                  <CountryIcon color="action" />
                </InputAdornment>
              }
            >
              <MenuItem value="">{t("select")}</MenuItem>
              {OPTIONS_BY_LANGUAGE[i18n.language]?.countries.map((country) => (
                <MenuItem key={country} value={country}>
                  {country}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Autocomplete
            freeSolo
            disabled={mode === "view"}
            options={professionOptions}
            inputValue={formData.profession || ""}
            onInputChange={(e, newValue) => handleInputChange("profession", newValue || "")}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("applicants.profession")}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <InputAdornment position="start">
                        <ProfessionIcon color="action" />
                      </InputAdornment>
                      {params.InputProps.startAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          <Autocomplete
            multiple
            options={positions}
            getOptionLabel={(option) => option.name}
            value={selectedPositionsObjects}
            onChange={handlePositionsChange}
            disabled={mode === "view"}
            loading={loadingPositions}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("applicants.positions")}
                placeholder={t("applicants.selectPositions")}
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  label={option.name}
                  {...getTagProps({ index })}
                  disabled={mode === "view"}
                />
              ))
            }
          />

          <Box>
            <Typography variant="subtitle2" gutterBottom>
              {t("applicants.cv")}
            </Typography>

            {canViewCv ? (
              <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                <Button
                  variant="outlined"
                  startIcon={<VisibilityIcon />}
                  onClick={() => window.open(formData.cvUrl, "_blank")}
                  fullWidth
                >
                  {t("applicants.viewCv")}
                </Button>
                {canUploadCv && (
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={handleDeleteCv}
                    disabled={loading}
                  >
                    {t("applicants.deleteCv")}
                  </Button>
                )}
              </Box>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {pendingCvDeletion && (
                  <Alert
                    severity="warning"
                    action={
                      <Button color="inherit" size="small" onClick={handleCancelDeletion}>
                        {t("cancel")}
                      </Button>
                    }
                  >
                    {t("applicants.cvWillDeleteOnSave")}
                  </Alert>
                )}

                {showUploadZone && (
                  <>
                    {selectedCvFile ? (
                      <Box
                        sx={{
                          border: "2px solid #4caf50",
                          borderRadius: 2,
                          p: 2,
                          backgroundColor: "#e8f5e9",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <AttachFileIcon color="success" />
                          <Box>
                            <Typography variant="body2" fontWeight="bold">
                              {selectedCvFile.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {t("applicants.cvWillUploadOnSave")}
                            </Typography>
                          </Box>
                        </Box>
                        <Button
                          size="small"
                          color="error"
                          onClick={handleRemoveSelectedFile}
                          disabled={loading}
                        >
                          {t("remove")}
                        </Button>
                      </Box>
                    ) : (
                      <></>
                    )}
                    {/* Aviso: reemplazar el CV elimina el resumen IA, con opción de mantenerlo */}
                    {selectedCvFile && selectedApplicant?.cvSummary && (
                      <Alert severity="warning" sx={{ mt: 1 }}>
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                          {t("applicants.cvSummaryWillDelete")}
                        </Typography>
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              checked={keepCvSummary}
                              onChange={(e) => setKeepCvSummary(e.target.checked)}
                            />
                          }
                          label={
                            <Typography variant="body2">
                              {t("applicants.cvSummaryKeep")}
                            </Typography>
                          }
                        />
                      </Alert>
                    )}
                    {selectedCvFile ? (
                      <></>
                    ) : (
                      <Box
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        sx={{
                          border: isDragging ? "2px dashed #1976d2" : "2px dashed #ccc",
                          borderRadius: 2,
                          p: 3,
                          textAlign: "center",
                          backgroundColor: isDragging ? "#e3f2fd" : "#fafafa",
                          cursor: "pointer",
                          transition: "all 0.3s ease",
                        }}
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept=".pdf,.doc,.docx"
                          style={{ display: "none" }}
                          onChange={handleFileSelect}
                        />
                        <CloudUploadIcon sx={{ fontSize: 48, color: "#1976d2", mb: 1 }} />
                        <Typography variant="body1" gutterBottom>
                          {t("applicants.cvDragAndDrop")}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {t("applicants.cvClickToSelect")}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                          {t("applicants.cvFormats")}
                        </Typography>
                      </Box>
                    )}
                  </>
                )}

                {!showUploadZone && (
                  <Typography variant="body2" color="text.secondary">
                    {t("applicants.cvNotLoaded")}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>{mode === "view" ? t("close") : t("cancel")}</Button>
        {mode !== "view" && (
          <Button onClick={handleSubmit} variant="contained" disabled={loading || !canSubmit}>
            {t("save")}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ApplicantModal;